'use client';

import {
  Box,
  Button,
  Divider,
  SystemIcon,
  Typography,
} from '@wishket/design-system';
import { ComponentProps } from 'react';
import { twMerge } from 'tailwind-merge';

import { formatViewCount } from '@/shared/utils';
import { PATH } from '@/shared/constants';
import { Link } from '@/shared/Components';

interface AuthorStatisticsProps {
  type: 'view' | 'like' | 'scrap';
  count: number;
}

export const AuthorStatistics = ({ type, count }: AuthorStatisticsProps) => {
  return (
    <Box className="flex items-center gap-1.5">
      {type === 'view' && (
        <SystemIcon name="small_eye" solid className="text-w-gray-600" />
      )}
      {type === 'like' && (
        <SystemIcon name="small_like" solid className="text-w-gray-600" />
      )}
      {type === 'scrap' && (
        <SystemIcon name="small_scrap" solid className="text-w-gray-600" />
      )}
      <Typography variant="body3" className="font-medium text-w-gray-600">
        {type === 'view' ? formatViewCount(count) : count.toLocaleString()}
      </Typography>
    </Box>
  );
};

interface TagLineBoxProps {
  tagLine: string;
  subscription: number;
  className?: string;
}

export const TagLineBox = ({
  tagLine,
  subscription,
  className,
}: TagLineBoxProps) => {
  return (
    <Box className={twMerge('items-center gap-2 text-w-gray-700', className)}>
      <Typography
        variant="body2"
        className="line-clamp-1 break-all desktop:typo-body1"
      >
        {tagLine}
      </Typography>
      <Divider isVertical className="h-3.5" />
      <Typography variant="body2" className="shrink-0 desktop:typo-body1">
        {`${subscription}명 알림 받는 중`}
      </Typography>
    </Box>
  );
};

interface SubscribeButtonProps {
  isSubscribed: boolean;
  isTooltipOpen?: boolean;
  handleSubscribe: () => void;
  openUnsubscribeDialog?: () => void;
}

export const SubscribeButton = ({
  isSubscribed,
  isTooltipOpen,
  handleSubscribe,
  openUnsubscribeDialog,
}: SubscribeButtonProps) => {
  return (
    <Box className="absolute right-5 top-5 tablet:right-[33px] tablet:top-[25px]">
      {isSubscribed ? (
        <Box className="relative">
          <Button
            size="sm"
            variant="outlined"
            isLeadingIconSolid
            leadingIcon="small_bell"
            className="min-w-[68px] tablet:w-[118px]"
            onClick={openUnsubscribeDialog}
          >
            <Typography variant="body2" className="flex">
              알림
              <span className="hidden tablet:block">&nbsp;받는 중</span>
            </Typography>
          </Button>
          {isTooltipOpen && <AuthorListTooltip />}
        </Box>
      ) : (
        <Button
          variant="solid"
          size="sm"
          leadingIcon="small_bell"
          className="group relative min-w-[68px] tablet:w-[118px]"
          onClick={handleSubscribe}
        >
          <Typography variant="body2">알림</Typography>
          <Typography variant="body2" className="hidden tablet:block">
            받기
          </Typography>
        </Button>
      )}
    </Box>
  );
};

const AuthorListTooltip = ({ className }: ComponentProps<'div'>) => {
  return (
    <Box className={twMerge('absolute right-0 top-12', className)}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="5"
        viewBox="0 0 10 5"
        fill="none"
        className="absolute -top-1 right-[28px] tablet:right-[54px]"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5 0L10 5L1.69887e-07 5L5 0Z"
          fill="white"
        />
      </svg>
      <Box className="w-[177px] break-keep rounded-xl bg-w-white px-4 py-3 shadow-graymedium ">
        <Link href={`${PATH.NOTIFICATION_SETTINGS}/?tab=subscription`}>
          <Typography
            variant="body2"
            className="font-medium text-primary hover:underline"
          >
            [알림 설정]
          </Typography>
        </Link>
        <Typography variant="body2" className="text-w-gray-900">
          에서 작가별 알림을 관리해 보세요.
        </Typography>
      </Box>
    </Box>
  );
};
